<script setup lang="ts">
import type { TDiamondItem, TJewelryItem } from '~/layers/catalog/data/types'

import CatalogPagination from '~/layers/catalog/components/CatalogPagination.vue'
import DiamondCard from '~/layers/catalog/components/DiamondCard.vue'
import DiamondsTable from '~/layers/catalog/components/DiamondsTable.vue'
import SkeletonCard from '~/layers/catalog/components/SkeletonCard.vue'
import { useCatalogDataProvider } from '~/layers/catalog/composables/catalogDataProvider'
import { useCatalogStore } from '~/layers/catalog/store/catalog'
import { useCatalogGridStore } from '~/layers/catalog/store/catalogGrid'
import { useBannersStore } from '~/layers/marketing/store/banners'

import { useCatalogPersistStore } from '../store/catalogPersist'
import { useCatalogScrollStore } from '../store/catalogScroll'
import CatalogSeoBlock from './CatalogSeoBlock.vue'

defineProps<{
  disableBanner?: boolean
  tableView?: boolean
}>()

const { pending, pendingMore, type, catalogTopEl } = useCatalogDataProvider()
const store = useCatalogStore()
const persistStore = useCatalogPersistStore()
const gridStore = useCatalogGridStore()
const catalogStore = useCatalogScrollStore()
const bannerStore = useBannersStore()
const route = useRoute()
const { currentMeta } = usePageMeta()

const activeCollections = ref<TFilterStoring | null>(null)

const isExistMetaBlock = computed<boolean>(() =>
  Boolean(currentMeta.value.text),
)
const lastViewedCardId = computed(() => catalogStore.lastViewedCardId)
const isTwoColumnForMobile = computed(
  () => persistStore.mobileGridLayout === 'two',
)

activeCollections.value = store.getFilter('collections')

function calcItemPage(index: number) {
  return Math.floor((index + 1) / PAGE_SIZE) + store.initialPage
}

function getProductKey(id: number, index: number) {
  return `${id}.${index}`
}

onBeforeRouteLeave((to, from, next) => {
  if (to.name?.toString().includes('pdp')) {
    const id =
      to.params?.productId ||
      to.params?.article ||
      to.fullPath.split('/').filter(Boolean).pop()
    if (id && typeof id === 'string') {
      catalogStore.setLastViewedCardId(id)
    }
  } else {
    catalogStore.clearLastViewedCardId()
  }
  next()
})

watch(
  [
    () => lastViewedCardId.value,
    () => gridStore.items,
    () => pending.value,
    () => bannerStore.loaded,
  ],
  () => {
    if (
      import.meta.client &&
      lastViewedCardId.value &&
      gridStore.items.length
    ) {
      nextTick(() => {
        const el = document.querySelector(
          `[data-id="${lastViewedCardId.value}"]`,
        )
        if (el && !pending.value && bannerStore.loaded) {
          el.scrollIntoView({ block: 'center' })
        }
      })
    }
  },
)

onMounted(() => {
  if (import.meta.client && lastViewedCardId.value && gridStore.items.length) {
    nextTick(() => {
      const el = document.querySelector(`[data-id="${lastViewedCardId.value}"]`)
      if (el && !pending.value && bannerStore.loaded) {
        el.scrollIntoView({ block: 'center' })
      }
    })
  }
})

watch(
  () => route.fullPath,
  () => {
    activeCollections.value = store.getFilter('collections')
  },
)
</script>

<template>
  <template v-if="pending || gridStore.items.length > 0">
    <div class="catalog__grid-wrap min-w-0">
      <div
        v-if="tableView"
        ref="catalogTopEl"
      >
        <DiamondsTable
          :items="gridStore.items"
          :pending="pending"
          :pending-more="pendingMore"
        />
      </div>
      <div
        v-else
        ref="catalogTopEl"
        :class="{ catalog__grid_cols: isTwoColumnForMobile }"
        class="catalog__grid"
      >
        <template
          v-for="(item, index) in gridStore.items"
          :key="`product.${getProductKey(item.id, index)}`"
        >
          <SkeletonCard v-if="pending" />
          <template v-else>
            <DiamondCard
              v-if="item.type === 'diamond'"
              :key="`diamond.${getProductKey(item.id, index)}`"
              :compact="isTwoColumnForMobile"
              :item="item as TDiamondItem"
              :data-id="item.code"
              show-favorite
            />
            <JewelryCard
              v-if="item.type === 'jewelry'"
              :key="`jewelry.${getProductKey(item.id, index)}`"
              :compact="isTwoColumnForMobile"
              :item="item as TJewelryItem"
              :data-id="item.url.split('/').filter(Boolean).pop()"
              :page="calcItemPage(index)"
              allow-touch-move
              show-favorite
            />
          </template>

          <client-only>
            <template v-if="item.type === 'diamond'">
              <CatalogBanner
                v-if="
                  !!$banners.catalog.getBannerDiamondsGrid(
                    index,
                    persistStore.mobileGridLayout,
                  ) && !disableBanner
                "
                :banner="
                  $banners.catalog.getBannerDiamondsGrid(
                    index,
                    persistStore.mobileGridLayout,
                  )
                "
              />
            </template>
            <template v-if="item.type === 'jewelry'">
              <CatalogBanner
                v-if="
                  !!$banners.catalog.getBannerJewelryGrid(
                    index,
                    persistStore.mobileGridLayout,
                    activeCollections,
                  ) && !disableBanner
                "
                :banner="
                  $banners.catalog.getBannerJewelryGrid(
                    index,
                    persistStore.mobileGridLayout,
                    activeCollections,
                  )!
                "
              />
            </template>
            <template #placeholder>
              <div style="display: none"></div>
            </template>
          </client-only>
        </template>
        <template v-if="pendingMore">
          <SkeletonCard
            v-for="n in 24"
            :key="`pending-card.${n}`"
          />
        </template>
      </div>
      <CatalogPagination v-if="gridStore.navigation.lastPage > 1" />

      <CatalogSeoBlock
        v-if="isExistMetaBlock"
        :text="currentMeta.text"
      />
    </div>
  </template>
  <div
    v-else
    class="catalog__empty-text"
  >
    <template v-if="type === 'diamonds'"> Нет подходящих бриллиантов </template>
    <template v-else> Нет подходящих украшений </template>
  </div>
</template>
